import { roleHasWritePermission } from "../../common/ag-grid-utils"
import memoize from "memoize-one"

import { bulkWorkflowActionWithLocation } from "../../actions/bulk-actions"
import { deleteSourceData, bulkUpdateSourceDataField, reshareGuestFormShares, softDeleteSourceData, bulkUpdateSourceDataFieldNoAutosave, undeleteSourceData } from "../actions"
import { openCicoQRLightboxModal, openCopyToModal, openFormShareModal, openImportToFieldFormModal, openChooseLaborRatesModal } from "../../components/modals/actions"
import { tResourceName } from "../../common/types"
import { tSourceData, tQueryParams, tResourceObject } from "../../dashboard-data/types"
import { tContext } from "../../components/custom-dashboards/types"
import { tButtonClickHandler, tButtonClickHandlerFactory, tButtonClickHandlerTuple, tBulkActionParams, tExtraButtonParams } from "../types"
import { getFormShareGuestVariants, getFormSharePermissions, updateRow } from "../../api"
import { getDefaultEmailMessage, fetchPreviousCollaborators, userRolesWithAdditionalSharePrivileges } from "../../components/guest-login/guest-login-utils"
import { getFlagEnabled } from "../../getFlagValue"
import { VIEW_ONLY, REQUEST_SIGNATURE, STORED_DATE_ONLY_FORMAT, BUNDLE_TABLE_NAME } from "../../common/constants"
import { IconAddCollaborator } from "@rhumbix/rmbx_design_system_web"
import { BulkViewMode } from "../../forms/BulkReviewForm/types"
import { FORM_BULK_ACTION, logUserAmplitudeEvent } from "../../common/amplitude-event-logging"
import { setNotificationMessage, wsConnect } from "../../websockets/actions"
import { getAuditHistoryById } from "../../actions/audit-history"
import { getSocketUrl } from "../../websocket-utils"
import { setSelectedVariant, triggerBulkPdfDownload } from "../../actions/form-view-actions"
import { IRowNode } from "ag-grid-community"
import { convertToFilename, filterCopyToTransformsBySchemaId, getBundleTransforms, getSelectedRowStatuses } from "../../common/ts-utils"
import { format } from "date-fns"
import { bulkAddSourceData, updateSourceDataRow } from "../../dashboard-data/actions/write"
import { getFormSummaryInfo } from "./utils"
import { iTimekeepingStatus } from "../../cached-data/types"
import { sourceDataAdded, sourceDataUpdated } from "../../dashboard-data/actions"
import { update_current_user } from "../../actions"
import { loadEntities } from "../../cached-data/actions"
import { getObjectId, getProjectIdFromTKRow } from "../../common/ag-grid-ts-utils"
import * as API from "../../api"
import { cloneDeep } from "lodash"

/**
 * Passes the currently selected PTO request rows to the weekly TK detail modal's fulfill
 * handler, which adds placeholder rows to the Timekeeping and Absences tabs.
 */
export const fulfillPTORequests: tButtonClickHandler = (e, { gridApi, weeklyTkDetailContext }) => {
    const selectedRows = gridApi?.getSelectedRows() ?? []
    // fulfillPTORequests is async; catch prevents an unhandled rejection if the thunk dispatch
    // throws unexpectedly, since tButtonClickHandler is synchronous and can't await the result.
    weeklyTkDetailContext?.fulfillPTORequests(selectedRows)?.catch(() => undefined)
}

/**
 * Passes all PTO request rows (regardless of selection) to the weekly TK detail modal's fulfill
 * handler, which adds placeholder rows to the Timekeeping and Absences tabs.
 */
export const fulfillAllPTORequests: tButtonClickHandler = (e, { gridApi, weeklyTkDetailContext }) => {
    const allRows: tResourceObject[] = []
    gridApi?.forEachNode(node => {
        if (node.data) allRows.push(node.data as tResourceObject)
    })
    // fulfillPTORequests is async; catch prevents an unhandled rejection if the thunk dispatch
    // throws unexpectedly, since tButtonClickHandler is synchronous and can't await the result.
    weeklyTkDetailContext?.fulfillPTORequests(allRows)?.catch(() => undefined)
}

export const deleteSelectedRows: tButtonClickHandler = (e, { args, context, gridApi }) => {
    const selectedRowsWithWritePermission = context.selectedRows.filter(row => !row.status || roleHasWritePermission(row.status, context.currentUser.user_role))

    const sourceDataToDelete: tSourceData = {
        [context.settings.resources[0]]: selectedRowsWithWritePermission,
    }

    if (!args?.no_autosave && !args?.local_state_update) {
        context.createModalAction({
            title: "Delete selected rows?",
            description: "This action cannot be undone",
            action: () => {
                context.dispatch(deleteSourceData(sourceDataToDelete, undefined, context.settings.gridSettings?.rowModelType === "serverSide"))
                gridApi.deselectAll()
            },
            close: () => context.createModalAction(null),
        })
    } else if (args?.no_autosave) {
        context.dispatch(softDeleteSourceData(sourceDataToDelete, gridApi.getSelectedNodes(), context.settings.gridSettings?.rowModelType === "serverSide"))
        gridApi.deselectAll()
    } else if (context.updateSourceDataCb) {
        context.updateSourceDataCb?.({ [context.settings.resources[0]]: selectedRowsWithWritePermission }, true)
        gridApi.deselectAll()
    }
}

export const bulkManageAccess: tButtonClickHandler = async (e, { context }) => {
    let itemType = context.settings.resources[0]

    // Resource name is pluralized and camelcase
    // but itemType should be lowercase without pluralization
    // front-end and back-end used different conventions for this
    // unfortunately so we have to translate
    if (itemType === "picklistItems") {
        itemType = "picklistitem"
    } else if (itemType == "companyTrades") {
        itemType = "companytrade"
    } else if (itemType == "companyClassifications") {
        itemType = "companyclassification"
    }

    const selectedRows = context.selectedRows.map(s => s.id)
    const itemName = selectedRows.length > 1 ? `${selectedRows.length} Items` : context.selectedRows[0].name
    const tableName = context.settings.tableName

    // Our display name is different than the table name in some cases
    // so mapping them here
    let title = ""
    if (tableName === "Company Trades") {
        title = "Trades"
    } else if (tableName === "Company Classifications") {
        title = "Classifications"
    } else {
        title = tableName
    }

    const defaultOpenRemapModal = (_: Record<string, any>) => {}
    const openRemapModal = context.sideRailContext.sideRailConfig.flow === "DATA_TABLE" ? context.sideRailContext.sideRailConfig.openRemapModal || defaultOpenRemapModal : defaultOpenRemapModal

    context.sideRailContext.enableSideRail({
        flow: "MANAGE_PICKLIST_ACCESS",
        type: itemType,
        ids: selectedRows,
        previousConfig: context.sideRailContext.sideRailConfig,
        itemTitle: `${title}, ${itemName}`,
        openRemapModal: openRemapModal,
        remapEnabled: selectedRows.length === 1,
    })
}

export const openDeletePicklistItemModal: tButtonClickHandler = (e, { context }) => {
    const { selectedRows, sideRailContext } = context
    const { sideRailConfig } = sideRailContext

    if (sideRailConfig.flow !== "DATA_TABLE" || sideRailConfig.resource !== "picklistItems" || selectedRows.length !== 1 || !sideRailConfig.openRemapModal) {
        return
    }

    sideRailConfig.openRemapModal({
        isOpen: true,
        isDelete: true,
        picklistItemId: selectedRows[0].id,
        projectIds: [],
    })
}

export const getOptInOutButtons = (params: tExtraButtonParams, field: string): tButtonClickHandlerTuple[] => {
    const optInAction = () =>
        bulkUpdateField(null, {
            args: {
                field,
                value: true,
            },
            ...params,
        })

    const optOutAction = () =>
        bulkUpdateField(null, {
            args: {
                field,
                value: false,
            },
            ...params,
        })

    return [
        ["Opt In", optInAction, "share", false, "Opt Into Emails"],
        ["Opt Out", optOutAction, "share", false, "Opt Out of Emails"],
    ]
}

export const getTextToggleButtons = (params: tExtraButtonParams): tButtonClickHandlerTuple[] => {
    const optInAction = () =>
        toggleTextAlerts(null, {
            args: {
                is_active: true,
            },
            ...params,
        })

    const optOutAction = () =>
        toggleTextAlerts(null, {
            args: {
                is_active: false,
            },
            ...params,
        })

    // we handle disabling at the dropdown level for these since both buttons are either enabled/disabled together
    // button actions params -> [label, action, icon, disabled, tooltip]
    return [
        ["Enable Text Alerts", optInAction, "circleCheck", false],
        ["Disable Text Alerts", optOutAction, "circleReject", false],
    ]
}

export const getActivationToggleButtons = (params: tExtraButtonParams): tButtonClickHandlerTuple[] => {
    const optInAction = () =>
        toggleEmployeeStatus(null, {
            args: {
                is_active: true,
            },
            ...params,
        })

    const optOutAction = () =>
        toggleEmployeeStatus(null, {
            args: {
                is_active: false,
            },
            ...params,
        })

    const { context } = params

    const activateTooltip = context.currentUser.features?.cico
        ? "Activate the employee(s) for your company. Also allows employee(s)" + " to log in to Rhumbix."
        : "Activate the employee(s) for your company. Also allows non-Workers" + " to log in to Rhumbix."

    const deactivateTooltip = "Deactivate the employee(s) for your company. Also prevents them" + " from being able to log in to Rhumbix."

    // we handle disabling at the dropdown level for these since both buttons are either enabled/disabled together
    // button actions params -> [label, action, icon, disabled, tooltip]
    return [
        ["Activate", optInAction, "view", false, activateTooltip],
        ["Deactivate", optOutAction, "hide", false, deactivateTooltip],
    ]
}

export const getLoginToggleButtons = (params: tExtraButtonParams): tButtonClickHandlerTuple[] => {
    const optInAction = () =>
        toggleLogin(null, {
            args: {
                is_active: true,
            },
            ...params,
        })

    const optOutAction = () =>
        toggleLogin(null, {
            args: {
                is_active: false,
            },
            ...params,
        })

    // we handle disabling at the dropdown level for these since both buttons are either enabled/disabled together
    // button actions params -> [label, action, icon, disabled, tooltip]
    return [
        ["Enable Login", optInAction, "circleCheck", false],
        ["Disable Login", optOutAction, "circleReject", false],
    ]
}

export const bulkUpdateField: tButtonClickHandler = (e, { args, context, gridApi, modalTitle = "", modalDescription = "", showWarningModal = false, updateUsingResponseData = false }) => {
    const resourceName = context.settings.resources[0]
    const selectedRowNodes = gridApi.getSelectedNodes()
    // for account settings we don't allow bulk updates to "other field forms" outside your company
    const sourceDataToUpdate: tSourceData = {
        [resourceName]:
            resourceName === "accountSettings"
                ? selectedRowNodes?.map(node => node?.data).filter(data => (args?.field?.startsWith("other_field_form") ? !data?.external_company : data))
                : context.selectedRows,
    }
    const typedArgs = args as tBulkActionParams
    const isSSRM = context.settings.gridSettings?.rowModelType === "serverSide"
    if (showWarningModal) {
        context.createModalAction({
            title: modalTitle,
            description: modalDescription,
            action: async () => {
                // on the Api Integration page, this fixes a glitch
                // where the unselectable rows are still highlighted once the deactivation occurs.
                // the calls to deselectAll() and clearFocusedCell() should prevent that
                if (resourceName === "apiIntegration") {
                    gridApi.deselectAll()
                    gridApi.clearFocusedCell()
                }
                await context.dispatch(bulkUpdateSourceDataField(sourceDataToUpdate, typedArgs, updateUsingResponseData, selectedRowNodes, isSSRM))
                if (resourceName === "apiIntegration") {
                    // force a refresh on the selected rows as the data passed down from
                    // cell renderer params doesn't seem to update without doing this
                    gridApi.refreshCells({ force: true, rowNodes: selectedRowNodes })
                }
            },
            buttonClass: "continueButton",
            close: () => {
                context.createModalAction(null)
            },
        })
    } else {
        // If the table isn't automatically saving, we take a slightly different route and only update the
        // table contents (i.e. no calls to the API)
        if (typedArgs.no_autosave) {
            context.dispatch(bulkUpdateSourceDataFieldNoAutosave(sourceDataToUpdate, typedArgs, selectedRowNodes, isSSRM))
        } else {
            context.dispatch(bulkUpdateSourceDataField(sourceDataToUpdate, typedArgs, false, selectedRowNodes, isSSRM))
        }
    }
}

const getWorkflowActionQueryParams = (context: tContext): tQueryParams => ({
    ...(context.settings.additionalQueryParams && context.settings.additionalQueryParams.history_for_status
        ? {
              history_for_status: context.settings.additionalQueryParams.history_for_status,
          }
        : {}),
    ...(context.settings.otherSettings.requestSpecificFields
        ? {
              field: context.settings.otherSettings.requestSpecificFields,
          }
        : {}),
})

/**
 * Takes in row context and returns an object with keys for availableVariants and commonVariantNames.
 * availableVariants are all names available across the selected stores (might not be available on all stores)
 * commonVariantNames are all names that available to every selected store
 */
const getVariantOptionInfo = (context: tContext): Record<string, string[]> => {
    const { selectedRows, listViewVariantNames, relatedVariantNames } = context

    const availableVariantNames = context?.settings?.isPrimary ? listViewVariantNames : relatedVariantNames

    // if we don't have variants, or we have more than 25 rows selected, no need for buttons
    // we disable bulk download if they select more than 25 rows, so no need to keep calculating.
    if (!availableVariantNames?.length) return {}

    // get all the lists of available variants from each selected row
    const variants: Array<string[]> = []
    const schemaNames: Set<string[]> = new Set()
    selectedRows.forEach(row => {
        if (row?.schema_name) {
            schemaNames.add(row.schema_name)
        }
        if (Array.isArray(row?.variants)) {
            variants.push(row.variants)
        }
    })

    // sort the variants, so the shortest list is first, so we keep the comparison as efficient as possible
    variants.sort((a, b) => a.length - b.length)

    // create a unique list of available names - we don't want to list duplicate names
    // the selector will make sure to pick the right variant for any given schema
    const uniqueAvailableNames = availableVariantNames?.reduce((uniqueNames: string[], name: string) => {
        if (!uniqueNames.includes(name)) uniqueNames.push(name)
        return uniqueNames
    }, [])

    // compare each list of variants and only return the ones shared across all lists
    const commonVariantNames = variants?.length ? variants?.reduce((names, newNames) => names.filter(name => newNames.includes(name))) : []

    const defaultOption = "Default Variant"
    return { availableVariants: [defaultOption, ...uniqueAvailableNames], commonVariantNames: commonVariantNames }
}

/**
 * Takes in row context and returns buttons for the list view download selected button
 */
export const getAvailableDownloadVariants: tButtonClickHandlerFactory = params => {
    const { context } = params
    const { selectedRows } = context

    // if we don't have variants, or we have more than 25 rows selected, no need for buttons
    // we disable bulk download if they select more than 25 rows, so no need to keep calculating.
    if (!selectedRows?.length || selectedRows?.length > 25) return []

    const variantInfo = getVariantOptionInfo(context)
    const { commonVariantNames, availableVariants } = variantInfo
    if (!availableVariants?.length) return []

    const defaultOption = "Default Variant"
    return availableVariants.map((variantName: string) => {
        const isDefault = variantName === defaultOption
        const action: tButtonClickHandler = (e, params) => {
            const downloadParams = { ...params }
            if (!isDefault) downloadParams["variantName"] = variantName
            bulkDownloadPdfs(e, downloadParams)
        }
        const isDisabled = !commonVariantNames?.includes(variantName) && !isDefault
        return [variantName, action, "download", isDisabled]
    })
}

/**
 * Takes in row context and returns buttons for the list view bundle button
 */
export const getAvailableBundleVariants: tButtonClickHandlerFactory = params => {
    const { context } = params
    const { selectedRows } = context

    if (!selectedRows?.length) return []

    const variantInfo = getVariantOptionInfo(context)
    const { commonVariantNames, availableVariants } = variantInfo
    if (!availableVariants?.length) return []

    const defaultOption = "Default Variant"
    return (
        availableVariants?.map((variantName: string) => {
            const isDefault = variantName === defaultOption
            const action: tButtonClickHandler = (e, params) => {
                const bundleParams = { ...params }
                if (!isDefault) bundleParams["variantName"] = variantName
                bundleForms(e, bundleParams)
            }
            const isDisabled = !commonVariantNames?.includes(variantName) && !isDefault
            return [variantName, action, "bundle", isDisabled]
        }) || []
    )
}

export const getWorkflowActionsHandlers: tButtonClickHandlerFactory = ({ context }) => {
    // An array of statuses that each of the selected rows could be transitioned to
    const statuses = getSelectedRowStatuses(context)

    const actionQueryParams = getWorkflowActionQueryParams(context)

    const sourceData = {
        [context.settings.resources[0]]: context.selectedRows,
    }

    return statuses.map(status => {
        const label = status
        const icon = "status"
        const action: tButtonClickHandler = (e, params) => {
            params.context.createModalAction({
                action: async () => {
                    await params.context.dispatch(
                        bulkWorkflowActionWithLocation(sourceData, actionQueryParams, {
                            action: status,
                            queryParams: actionQueryParams,
                        })
                    )
                    if (context?.notificationInfo?.socket) {
                        // subscribe to project channels so we properly update the download button
                        context?.notificationInfo?.socket.send(
                            JSON.stringify({
                                message: "Subscribe User to Project Channels",
                                type: "add.to.channels",
                                project_ids: getFlagEnabled("WA-8572-fix-pdf-download") ? context.selectedRows?.map(row => getObjectId(row.project)) : context.selectedRows?.map(row => row.project),
                            })
                        )
                    }
                },
                close: () => {
                    params.context.createModalAction(null)

                    // Need to deselect rows so that the workflow actions
                    // refresh to reflect the status change
                    // we only have the gridApi if using list-view toolbar, in right rail, we don't
                    params?.gridApi?.deselectAll()
                },
                title: `Are you sure you want to "${status}" selected rows?`,
                description: "This action cannot be undone",
            })
        }

        return [label, action, icon]
    })
}

/**
 * Get the proper buttons/tooltips for field forms based on the selected rows in the table.
 * We support single edit/view (assuming they have permission) with or without a project selected
 * We support bulk view with or without a project selected as long as all schemas match
 * We support bulk editing if all selected forms have the same project, or single project is selected in the filter
 * @param params - ag-grid context with info on selected rows
 * @returns Form Review Action Buttons
 */
export const getFormReviewActionButtons: tButtonClickHandlerFactory = params => {
    const { context } = params
    const { referenceableData, selectedRows, current_project_id } = context
    // if nothing is selected, there is nothing to view/modify
    if (!selectedRows?.length) return []

    const isBulkEdit = selectedRows.length > 1
    // getFormSummaryInfo will go through all selected rows and return the necessary info to set the buttons
    const memoizedGetSummary = memoize(getFormSummaryInfo)
    const { modifyDisabled, viewDisabled, editTooltip, viewTooltip, selectedStatuses, selectedHashes, checkedSchemas, selectedProjectIds, selectedStoreIds } = memoizedGetSummary(
        selectedRows,
        current_project_id,
        referenceableData
    )

    const listViewName = context.settings.tableName
    const { schema_name: schemaName } = selectedRows[0]
    const isBundle = context.settings.tableName === BUNDLE_TABLE_NAME

    const hasSingleProjectSelectedInFilter = Array.isArray(current_project_id) && current_project_id?.length === 1
    let projectId: number | undefined = undefined
    // get the project name from the selected project id and the referenceable data
    projectId = !Array.isArray(current_project_id) ? current_project_id : hasSingleProjectSelectedInFilter ? current_project_id[0] : undefined

    const selectedProjectId = selectedProjectIds.size === 1 ? getObjectId(selectedProjectIds.values().next().value as number) : undefined
    // if they don't have a project filtered but only one project id is used across all forms
    // or it is a GC and only forms from one sub's project are selected, swap projectId to the sub's project
    // this will allow the GC to update the PCO picker for a single sub's forms. Cannot bulk edit PCO across subs.
    if (selectedProjectId !== projectId) {
        projectId = selectedProjectId
    }
    const project = projectId ? referenceableData?.projects?.[projectId] : {}
    const projectName = project?.name ?? ""

    const modes: BulkViewMode[] = ["View", "Modify"]
    return modes.map(mode => {
        const isModify = mode === "Modify"
        const label = isModify ? "Edit" : "View"
        const icon = isModify ? "edit" : "view"
        const disabled = isModify ? modifyDisabled : viewDisabled
        const tooltip = isModify ? editTooltip : viewTooltip
        // we need to pass this info along to amplitude. We also pass it to the right rail for the internal toolbar
        const amplitudeMetrics = {
            bulk_action: mode,
            form_statuses: [...selectedStatuses],
            project_name: projectName,
            schema_name: schemaName,
            schema_hash: [...selectedHashes][0],
            total_forms: selectedStoreIds.length,
        }

        const sideRailSettings = {
            schemaId: [...checkedSchemas][0], // all schemas match, so just grab the id of the first
            mode: mode,
            extraBtnParams: params,
            modifyButtonDisabled: modifyDisabled,
            amplitudeMetrics,
            listViewFields: context.settings.otherSettings.requestSpecificFields,
            listViewContext: params.context.settings,
            gridId: context.selectedRows[0]?.gridId,
        }

        const action: tButtonClickHandler = () => {
            if (!isBulkEdit) {
                context.sideRailContext.enableSideRail({
                    ...sideRailSettings,
                    flow: "FIELD_FORMS",
                    formId: context.selectedRows[0]?.id,
                    gridApi: params.gridApi,
                    isBundle: isBundle,
                    projectId: projectId,
                    listViewContext: context.settings,
                    setSelectedTab: context.setSelectedTab,
                    enableSideRail: context.sideRailContext.enableSideRail,
                })
            } else {
                context.sideRailContext.enableSideRail({
                    ...sideRailSettings,
                    flow: "BULK_REVIEW",
                    selectedStoreIds: selectedStoreIds,
                    gridApi: params.gridApi,
                    listViewName,
                    // a GC won't have a sub's full project - we use sub project id for PCO picker
                    selectedProject: project ?? { id: projectId },
                })
            }
            // log the event to amplitude
            logUserAmplitudeEvent(FORM_BULK_ACTION, amplitudeMetrics)
        }
        return [label, action, icon, disabled, tooltip]
    })
}

export const getFormVariants: tButtonClickHandlerFactory = params => {
    const currentVariant: string = params.args?.extraArgs?.selectedVariantName
    const availableVariants: string[] = params.args?.extraArgs?.formVariants
    return availableVariants
        ? availableVariants
              .filter(v => v !== currentVariant)
              .map(v => {
                  const label: string = v
                  const action: tButtonClickHandler = () => {
                      params.context.dispatch(setSelectedVariant(v))
                  }
                  const icon = "view"
                  return [label, action, icon]
              })
        : []
}

/**
 * Launch Copy To in the side rail. If there are multiple transforms available for the selected
 * form,
 * @param {tMouseEvent<HTMLButtonElement> | null} e Click event.
 * @prop {Object} params View params.
 */
export const copyToForm: tButtonClickHandler = (e, params) => {
    const allCopyToTransforms = params.context.settings.otherSettings.relatedCopyToTransforms
    const schemaId = params.context.selectedRows[0]?.schema
    // we only want to look at transforms available for the schema related to our selection
    const relatedCopyToTransforms = filterCopyToTransformsBySchemaId(allCopyToTransforms, schemaId)
    const selectedRowIds: number[] = params.context.selectedRows.map(row => row.id)
    const projectId = Array.isArray(params.context.current_project_id) ? params.context.current_project_id[0] : params.context.current_project_id

    if (relatedCopyToTransforms.length > 1) {
        params.context.dispatch(
            openCopyToModal({
                selectedRowIds: selectedRowIds,
                schemaId: schemaId,
                projectId: projectId,
            })
        )
    } else {
        params.context.sideRailContext.enableSideRail({
            flow: "FIELD_FORMS",
            mode: "Create",
            isNew: true,
            isTransform: true,
            selectedRowIds: selectedRowIds,
            schemaId: params.context.selectedRows[0].schema,
            relatedCopyToTransform: relatedCopyToTransforms[0],
            projectId: projectId,
            extraBtnParams: params,
            gridApi: params.gridApi,
            listViewFields: params.context.settings.otherSettings.requestSpecificFields,
            listViewContext: params.context.settings,
            gridId: params.context.selectedRows[0]?.gridId,
            setSelectedTab: params.context.setSelectedTab,
            enableSideRail: params.context.sideRailContext.enableSideRail,
        })
    }
}

/**
 * Launch bundle form
 * @param {tMouseEvent<HTMLButtonElement> | null} e Click event.
 * @prop {Object} params View params.
 */
export const bundleForms: tButtonClickHandler = (e, params) => {
    const { context, variantName } = params
    const { current_project_id: currentProjectId, selectedRows } = context
    const { otherSettings } = context?.settings ?? {}
    if (otherSettings === undefined || !Object.keys(otherSettings).length) return
    const bundleTransforms = otherSettings?.bundleTransforms
    const bundleSchemas = otherSettings?.related_schemas

    const bundleTransform = getBundleTransforms(context, bundleTransforms)
    // there should only be one item in the set - so grab that and set as the transformId
    const transformId = bundleTransform?.values().next().value?.id
    if (!transformId) {
        alert("The selected forms cannot be bundled")
        return
    }
    const projectId = Array.isArray(currentProjectId) ? currentProjectId[0] : currentProjectId

    const selectedRowIds: number[] = selectedRows.map(row => row.id)
    context.sideRailContext.enableSideRail({
        flow: "BUNDLE",
        selectedRowIds: selectedRowIds,
        transformId: transformId,
        bundleSchemaId: bundleSchemas[0].id,
        projectId: projectId,
        schemaId: selectedRows[0].schema,
        extraBtnParams: params,
        setSelectedTab: context.setSelectedTab,
        enableSideRail: context.sideRailContext.enableSideRail,
        bundleVariantName: variantName ?? undefined,
        listViewFields: context.settings.otherSettings.requestSpecificFields,
    })
}

export const bulkDownloadPdfs: tButtonClickHandler = (e, { context, variantName, isHistory = false }) => {
    // extract the ids from the selected rows
    const selectedStoreIds: number[] = []
    const historyIds: number[] = []
    context.selectedRows.forEach(r => {
        selectedStoreIds.push(r.id)
        if (getFlagEnabled("WA-8216-historic-pdf-download") && r?.history_id !== undefined && isHistory) {
            historyIds.push(r.history_id)
        }
    })
    // make sure the websocket is activated to receive the download
    const { notificationInfo } = context
    if (!notificationInfo?.connected) {
        // if the socket got disconnected try and reconnect it
        context.dispatch(wsConnect(getSocketUrl("company_form_stores")))
    }
    // generate a filename based on the list view title so we know what to call the zip file
    const filename = `${convertToFilename(context.settings.listViewTitle)}_${format(new Date(), STORED_DATE_ONLY_FORMAT)}.zip`
    context.dispatch(triggerBulkPdfDownload(selectedStoreIds, filename, variantName, historyIds))
}

/**
 * Launch the row-specific share modal.
 * @param {tMouseEvent<HTMLButtonElement> | null} e Click event.
 * @prop {Object} params
 */
export const shareLinkToForm: tButtonClickHandler = async (e, { context }) => {
    const currentUserFullName = `${context.currentUser.first_name} ${context.currentUser.last_name}`
    const companyName = context.currentUser.company
    const projectsList = context.selectedRows.map(ea => ea.project_name)
    const projectIDs = context.selectedRows.map(ea => getObjectId(ea.project))
    const uniqueProjectIDs = [...new Set(projectIDs)]
    const selectedForms: number[] = getFlagEnabled("WA-8951-fix-email-schema-name") ? [] : context.selectedRows.map(ea => ea.id)
    const schemaIds: number[] = getFlagEnabled("WA-8951-fix-email-schema-name") ? [] : Object.keys(context.referenceableData.companyFormSchemas).map(id => parseInt(id))

    if (getFlagEnabled("WA-8951-fix-email-schema-name")) {
        // get the form and schema ids from the selected rows
        context.selectedRows.forEach(ea => {
            selectedForms.push(ea.id)
            if (schemaIds.indexOf(ea.schema) === -1) schemaIds.push(ea.schema)
        })
    }
    let schemaName = ""
    if (getFlagEnabled("WA-8951-fix-email-schema-name")) {
        // the single space string is a bit hacky, but it provides a truthy value so we properly set the default
        // message in the modal to say "multiple forms" - the space will collapse in the title
        schemaName = schemaIds.length === 1 && context.referenceableData.companyFormSchemas?.[schemaIds[0]] ? context.referenceableData.companyFormSchemas[schemaIds[0]].name : " "
    } else {
        schemaName = context.referenceableData.companyFormSchemas[schemaIds[0]].name
    }
    let fetchError = null
    let uniquePreviousCollaborators

    try {
        uniquePreviousCollaborators = await fetchPreviousCollaborators(uniqueProjectIDs, schemaIds)
    } catch (e) {
        // error object needs a type - this will be fixed in typescript >4.x
        const error = e as any
        fetchError = error.message
    }

    let formSharePermissions = {} // "VIEW_ONLY" for all forms
    let formShareVariants = {}
    if (userRolesWithAdditionalSharePrivileges.includes(context.currentUser.user_role)) {
        // Get the available permissions for the selected forms. This can be a slow operation if there are many
        // forms with many different statuses to check, but hopefully that case is rare
        try {
            formSharePermissions = await getFormSharePermissions({ form_ids: selectedForms })
        } catch (e) {
            // error object needs a type - this will be fixed in typescript >4.x
            const error = e as any
            fetchError = error.message
        }
        // get the variants available for the selected stores
        try {
            formShareVariants = await getFormShareGuestVariants({ form_ids: selectedForms })
        } catch (e) {
            // error object needs a type - this will be fixed in typescript >4.x
            const error = e as any
            fetchError = error.message
        }
    }

    context.dispatch(openFormShareModal(uniquePreviousCollaborators, projectsList, selectedForms, currentUserFullName, companyName, schemaName, fetchError, formSharePermissions, formShareVariants))
}

/**
 * Launch the row-specific Collaborators view in the Side Rail.
 * @param {tMouseEvent<HTMLButtonElement> | null} e Click event.
 * @prop {Object} context View context.
 */
export const openSideRailCollaboratorsForSelectedRows: tButtonClickHandler = async (e, { context }) => {
    if (context.sideRailContext.sideRailEnabled) return
    const selectedRows = context.selectedRows.map(s => s.id)
    const resource = "guestFormShares"

    // This comes from modify production value getters
    context.sideRailContext.enableSideRail({
        flow: "DATA_TABLE",
        config: {
            formIds: selectedRows,
            useBasicHeader: true,
            hideApplyButton: true,
            cancelButtonText: "Done",
            headerIcon: IconAddCollaborator(),
        },
        filters: [],
        resource,
        title: `${context.settings.tableName} Collaborators`,
    })
}

/**
 * From the Collaborators view in the side rail, reshare the
 * guest share forms and trigger the invitation e-mails for the
 * selected rows
 * @param {tMouseEvent<HTMLButtonElement> | null} e Click event.
 * @prop {Object} context View context.
 */
export const reshareGuestShareForm: tButtonClickHandler = (e, { context }) => {
    if (context.selectedRows.length < 1) return

    const form_ids: number[] = []
    const guests: any = []
    const currentUserFullName = `${context.currentUser.first_name} ${context.currentUser.last_name}`
    const companyName = context.currentUser.company

    context.selectedRows.forEach(row => {
        form_ids.push(row.form)
        guests.push({ email_address: row.guest.email, permissions: row.permissions })
    })

    // The interpreter believes that companyFormStores contains iCompanyFormStore objects, but the actual
    // objects have more fields than the type would indicate (including two fields that we want to use), so
    // this is being cast to any
    const companyFormStore: any = context.referenceableData.companyFormStores[form_ids[0]]

    const projectName = companyFormStore ? companyFormStore["project_name"] : ""

    const schema = context.referenceableData.companyFormSchemas[companyFormStore["schema"]]
    const formType = schema ? schema.name : ""

    // We will use the default generated e-mail message, with no opportunity to change it
    const email_body = getDefaultEmailMessage(currentUserFullName, companyName, formType, form_ids, projectName)

    context.dispatch(
        reshareGuestFormShares({
            form_ids: [...new Set(form_ids)],
            guests: guests,
            email_body: email_body,
        })
    )
}

/**
 * From the Collaborators view in the side rail, change permissions
 * on the selected guest form shares to the value selected in the dropdown.
 * @param {tMouseEvent<HTMLButtonElement> | null} e Click event.
 * @prop {Object} context View context.
 */
export const updateGuestShareFormPermissions: tButtonClickHandlerFactory = params => {
    // TODO: This function can fire when the user selects rows on the main table as well as the right rail,
    // which is unnecessary - and when it does, the context.selectedRows are not guestFormShares. Can we
    // stop it before it gets rolling?
    const updateMethod = (permissions: string, context: any) => {
        const sourceDataToUpdate: tSourceData = {
            ["guestFormShares"]: context.selectedRows,
        }
        const args = {
            field: "permissions",
            value: permissions,
            resourceToId: "guestFormShares",
        } as tBulkActionParams
        params.context.dispatch(bulkUpdateSourceDataField(sourceDataToUpdate, args))
    }

    const updateToViewOnly: tButtonClickHandler = (e, { context }) => updateMethod(VIEW_ONLY, context)
    const updateToRequestSignature: tButtonClickHandler = (e, { context }) => updateMethod(REQUEST_SIGNATURE, context)

    let isRequestSignatureEnabled = false

    if (params.sideRailContext?.sideRailConfig.flow === "DATA_TABLE") {
        if (params.context.selectedRows) {
            isRequestSignatureEnabled = true
            params.context.selectedRows.forEach(row => {
                // The available_permissions field is populated if the check_available_permissions
                // flag was passed to the query
                const perms = row.available_permissions
                if (!perms || !perms.find((p: any) => p === REQUEST_SIGNATURE)) {
                    isRequestSignatureEnabled = false
                }
            })
        }
    }

    // The View Only option is always available to any form that can trigger the Collaborators right rail
    const options: tButtonClickHandlerTuple[] = [["View Only", updateToViewOnly, "settings"]]

    // The Request Signature option is only available if this form has all share permissions
    if (isRequestSignatureEnabled) {
        options.push(["Request Signature", updateToRequestSignature, "settings"])
    }

    return options
}

/**
 * From the Collaborators view in the side rail, delete a guest form share, denying the
 * guest access to the specified form
 * @param {tMouseEvent<HTMLButtonElement> | null} e Click event.
 * @prop {Object} context View context.
 */
export const deleteGuestShareForm: tButtonClickHandler = (e, { context }) => {
    const sourceDataToDelete: tSourceData = {
        [context.settings.resources[0]]: context.selectedRows,
    }

    context.dispatch(deleteSourceData(sourceDataToDelete, undefined, context.settings.gridSettings?.rowModelType === "serverSide"))
}

export const openHistory: tButtonClickHandler = (e, { context }) => {
    const resource = context.settings.resources[0]
    const id = context.selectedRows[0].id
    const requestSpecificFields = context.settings.otherSettings.requestSpecificFields
    const queryParams = requestSpecificFields ? { field: requestSpecificFields } : {}

    context.dispatch(getAuditHistoryById(resource, id, queryParams))
}

/**
 * Archive function used in Right Rail to soft-delete selected rows
 * Save is executed when clicking 'OK' in confirmation modal; does not auto-save
 * Permissions for archiving are aligned with current deletion permissions
 * Archiving is achieved by setting the `deleted_on` datestamp, but record is retained and
 * can be accessed by clicking the 'show deleted/archived items' checkbox in the filter
 */
export const archiveSelectedRows: tButtonClickHandler = (e, { context }) => {
    const selectedRowsWithWritePermission = context.selectedRows.filter(row => !row.status || roleHasWritePermission(row.status, context.currentUser.user_role))

    const sourceDataToDelete: tSourceData = {
        [context.settings.resources[0]]: selectedRowsWithWritePermission,
    }

    context.createModalAction({
        title: "Archive selected items?",
        description: "These items can be accessed by clicking the 'Show Archived Items' checkbox in the filter.",
        action: () => {
            context.dispatch(deleteSourceData(sourceDataToDelete, undefined, context.settings.gridSettings?.rowModelType === "serverSide"))
        },
        close: () => context.createModalAction(null),
    })
}

/**
 * Function to restore rows that were archived by the archiveSelectedRows action
 */
export const unarchiveSelectedRows: tButtonClickHandler = (e, { context }) => {
    const selectedRowsWithWritePermission = context.selectedRows.filter(row => !row.status || roleHasWritePermission(row.status, context.currentUser.user_role))

    if (selectedRowsWithWritePermission.length === 0) {
        const isPlural = context.selectedRows.length !== 1
        context.createModalAction({
            title: `Cannot restore selected item${isPlural ? "s" : ""}`,
            description: `You do not have permission to update ${isPlural ? "these items" : "this item"}.`,
            close: () => context.createModalAction(null),
        })
        return
    }

    const sourceDataToRestore: tSourceData = {
        [context.settings.resources[0]]: selectedRowsWithWritePermission,
    }

    const isPlural = selectedRowsWithWritePermission.length !== 1

    context.createModalAction({
        title: `Restore selected item${isPlural ? "s" : ""}?`,
        description: `${isPlural ? "These items" : "This item"} will no longer be archived, and can be viewed and edited as before.`,
        action: () => {
            context.dispatch(undeleteSourceData(sourceDataToRestore, undefined, context.settings.gridSettings?.rowModelType === "serverSide"))
        },
        close: () => context.createModalAction(null),
    })
}

/**
 * Button click handler for the Add Row button that is available when one or more group rows is selected.
 * While the table-level Add Row button simply adds a blank row to the table, this button handler
 * adds a row under each selected group by populating it with the value associated with that group
 *
 * @param e         Not used
 * @param gridApi   The AG Grid gridApi, used to look up the selected group rows and colDefs for this table
 * @param context   The overall context for this operation, used to provide more info on the table
 */
export const addNewRowToGroup: tButtonClickHandler = (e, { gridApi, context }) => {
    const groupRows = gridApi.getSelectedNodes().filter((node: IRowNode) => node.group)

    const colDefs = (gridApi.getColumns() || [])?.map(column => column.getColDef())

    if (groupRows?.length > 0) {
        const rowsToAdd: tResourceObject[] = []
        groupRows.forEach((groupRow: IRowNode) => {
            const row: tResourceObject = {}

            // The key comes in as a string, but check for a number just in case
            const id = groupRow.key ? (typeof groupRow.key === "string" ? parseInt(groupRow.key) : null) : null
            const field = groupRow.field ? groupRow.field.replace("/", "") : null

            if (id && field) {
                row[field] = typeof id === "string" ? parseInt(id) : id
            }

            rowsToAdd.push(row)

            // Make sure that the field we're populating will not get overwritten with a
            // default in prepNewRow. Setting its default property to undefined will tell prepNewRow
            // not to stick a default into the field
            const colForRelevantField = colDefs.find((col: any) => col.field === groupRow.field)
            if (colForRelevantField) colForRelevantField.cellEditorParams.default = undefined

            // Make sure this group row is expanded, so that the user can see the added rows
            groupRow.expanded = true
        })

        if (context.updateSourceDataCb) {
            bulkAddSourceData(
                context.settings.resources[0],
                colDefs,
                rowsToAdd,
                context.settings.otherSettings.rowLevelValidators,
                context.settings.otherSettings.hiddenColumnDefaults,
                context.groupKeyInfo,
                context.filters,
                context.updateSourceDataCb,
                context
            )
        }
    }
}

/**
 * While editing a field form, the user can import data from a time card or another form
 * if an applicable transform is available. This button handler kicks off the process
 */
export const importFromFormOrTimeCard: tButtonClickHandler = (e, { context }) => {
    let schemaId, projectId, companyFormStoreId
    const transformsForImport = context.settings.otherSettings.transformsForImport

    // If there are no rows selected in the list view, then this action must come from a
    // Create flow
    if (!context.selectedRows || context.selectedRows.length === 0) {
        if (
            !transformsForImport?.length ||
            (Array.isArray(context.filters.projectId) && !context.filters.projectId?.length) ||
            (!Array.isArray(context.filters.projectId) && !context.filters.projectId)
        )
            return
        schemaId = transformsForImport[0].to_schema

        // The filters must have a project selected in order for the Create flow to be
        // available - use that to grab the projectId
        projectId = Array.isArray(context.filters.projectId) ? context.filters.projectId[0] : context.filters.projectId
    } else {
        // Otherwise, get the data the modal needs from that selected row
        const selectedCompanyFormStore = context.selectedRows[0]

        schemaId = selectedCompanyFormStore.schema
        projectId = getObjectId(selectedCompanyFormStore.project)
        companyFormStoreId = selectedCompanyFormStore.id
    }

    context.dispatch(
        openImportToFieldFormModal({
            schemaId: schemaId,
            projectId: projectId,
            companyFormStoreId: companyFormStoreId,
            transformsForImport: transformsForImport,
        })
    )
}

export const duplicateRow: tButtonClickHandler = (e, { context, gridApi }) => {
    const colDefs = (gridApi.getColumns() || [])?.map(column => column.getColDef())
    // Use gridApi.getSelectedNodes() to get fresh data instead of context.selectedRows which can be stale
    const sourceRowData = getFlagEnabled("WA-9079-wage-rates") ? gridApi.getSelectedNodes()[0]?.data : context.selectedRows[0]

    if (!sourceRowData) return

    let newRowName = `${sourceRowData.name} copy`
    const existingNodeNames = new Set()
    gridApi.forEachNode(n => {
        existingNodeNames.add(n.data.name)
    })
    let copyCounter = 2
    while (existingNodeNames.has(newRowName)) {
        newRowName = `${sourceRowData.name} copy ${copyCounter}`
        copyCounter += 1
    }

    // This is a little weird -- we need to indicate that it's a new row in order for write.ts
    // to trigger an API POST call instead of an update. However, if there's a gridId in the object,
    // then it will think that the row already existed in the table and trigger a source data update.
    // I added the `forceAdd` parameter here (and only here) to tell it to do a source data add.
    // This also insulates us from a random new bug in some other table elsewhere.
    const newRowData = {
        ...sourceRowData,
        name: newRowName,
        newRow: true,
        forceAdd: true,
    }
    if (newRowData.id) delete newRowData.id
    // Fix: gridId === 0 is falsy, so use != undefined (null or undefined) check to handle first row
    if (getFlagEnabled("WA-9079-wage-rates") ? newRowData.gridId != undefined : newRowData.gridId) delete newRowData.gridId

    context.dispatch(
        updateSourceDataRow(
            context.settings.resources[0],
            newRowData,
            colDefs,
            context.settings.otherSettings.rowLevelValidators,
            !!context.settings.otherSettings?.suppressAutoSync,
            context,
            null,
            false
        )
    )
}

/**
 * Duplicate multiple selected rows at once.
 * Requires updateSourceDataCb to be available on the context for batch updates.
 * Deselects all rows after duplication to avoid confusion as row indices change.
 */
export const duplicateRows: tButtonClickHandler = (e, { context, gridApi }) => {
    const selectedRows = gridApi.getSelectedNodes().map(node => node.data)
    if (!selectedRows || selectedRows.length === 0) return
    if (!context.updateSourceDataCb) return

    // Collect all existing names to avoid collisions (including between copies)
    const existingNames = new Set<string>()
    gridApi.forEachNode(n => {
        existingNames.add(n.data.name)
    })

    const newRows = selectedRows.map(sourceRowData => {
        let newRowName = `${sourceRowData.name} copy`
        let copyCounter = 2
        while (existingNames.has(newRowName)) {
            newRowName = `${sourceRowData.name} copy ${copyCounter}`
            copyCounter += 1
        }
        existingNames.add(newRowName)

        const newRowData = {
            ...cloneDeep(sourceRowData),
            name: newRowName,
            newRow: true,
            forceAdd: true,
        }
        if (newRowData.id) delete newRowData.id
        if (newRowData.gridId != undefined) delete newRowData.gridId
        return newRowData
    })

    const resource = context.settings.resources[0]
    context.updateSourceDataCb({ [resource]: newRows })

    // Deselect to avoid confusion as row indices change after duplication
    gridApi.deselectAll()
}

/**
 * Duplicate the selected rate tables via a server-side deep copy.
 * Calls the duplicate endpoint, adds the new rows to the grid,
 * and shows a success notification.
 */
export const duplicateRateTables: tButtonClickHandler = (e, { context, gridApi }) => {
    const selectedIds = context.selectedRows.map((r: tResourceObject) => r.id).filter(Boolean) as number[]
    if (!selectedIds.length) return

    const singleOrPlural = selectedIds.length === 1 ? "Rate Table" : "Rate Tables"
    const resource = context.settings.resources[0]
    API.duplicateRateTables({ ids: selectedIds })
        .then((response: tResourceObject[]) => {
            context.dispatch(sourceDataAdded({ [resource]: response }))
            gridApi.deselectAll()
            context.dispatch(
                setNotificationMessage(`${singleOrPlural} duplicated successfully`, "success", {
                    autoCloseSeconds: 3,
                })
            )
        })
        .catch((error: Error) => handleError(error, context))
}

/**
 * Bulk reprice selected field form stores by re-running rate matching via the backend.
 * Shows a confirmation modal before proceeding. Updates the grid rows in-place with
 * the serialized response and shows a notification with the reprice summary.
 */
export const repriceFieldFormRates: tButtonClickHandler = (e, { context }) => {
    const selectedIds = context.selectedRows.map((r: tResourceObject) => r.id).filter((id): id is number => typeof id === "number")
    if (!selectedIds.length) return

    const fields: string[] = context.settings.otherSettings?.requestSpecificFields ?? []

    context.createModalAction({
        title: "Reprice selected forms?",
        description: "Matched rates will be updated for all selected forms. This cannot be undone.",
        action: async () => {
            context.createModalAction(null)
            try {
                const result = await API.repriceFieldForms({ ids: selectedIds, fields })
                if (result.updated_stores?.length) {
                    const updatedRows = result.updated_stores.map((responseRow: tResourceObject) => {
                        const gridRow = context.selectedRows.find((r: tResourceObject) => r.id === responseRow.id)
                        return { ...responseRow, gridId: gridRow?.gridId }
                    })
                    context.dispatch(sourceDataUpdated({ companyFormStores: updatedRows }))
                }
                const repriceCounts: string[] = []
                if (result.updated) repriceCounts.push(`${result.updated} updated`)
                if (result.no_changes) repriceCounts.push(`${result.no_changes} already up to date`)
                if (result.no_permission) repriceCounts.push(`${result.no_permission} skipped (no permission)`)
                const errorCount = Object.keys(result.errors ?? {}).length
                if (errorCount) repriceCounts.push(`${errorCount} failed`)
                const summary = repriceCounts.length ? repriceCounts.join(", ") : "No changes"
                context.dispatch(setNotificationMessage(`Reprice complete. ${summary}.`, "success", { autoCloseSeconds: 5 }))
            } catch (error) {
                handleError(error as Error, context)
            }
        },
        close: () => context.createModalAction(null),
    })
}

export const deleteStatus: tButtonClickHandler = (e, { context, sourceData }) => {
    context.createModalAction({
        isStatusRemapModal: true,
        sourceStatus: context.selectedRows[0],
        allStatuses: sourceData.timekeepingStatuses as iTimekeepingStatus[],
        close: () => context.createModalAction(null),
    })
}

export const unhideStatus: tButtonClickHandler = (e, { context, gridApi }) => {
    updateRow("timekeepingStatuses", { ...context.selectedRows[0], is_hidden: false })
        .then(response => {
            context.dispatch(
                sourceDataUpdated({
                    timekeepingStatuses: [{ ...response, gridId: context.selectedRows[0].gridId }],
                })
            )
            if (getFlagEnabled("WA-8344-tk-status-fix")) {
                context.dispatch(
                    update_current_user({
                        ...context.currentUser,
                        employee: {
                            ...context.currentUser.employee,
                            allowed_tk_statuses: [...(context.currentUser.employee.allowed_tk_statuses || []), context.selectedRows[0].name],
                        },
                    })
                )
                context.dispatch(
                    loadEntities({
                        timekeepingStatuses: {
                            ...context.entities.timekeepingStatuses.objects,
                            [response.name]: response,
                        },
                    })
                )
            }
            gridApi.refreshCells({ force: true })
        })
        .catch(error => {
            alert(error)
        })
}

export const openCicoDetailsForSelectedRows: tButtonClickHandler = (e, { context, weeklyTkDetailContext }) => {
    // props.data gets stale if we have just created the row, so we need to find
    // the current row data in the state.
    const { referenceableData, selectedRows } = context
    const sourceObject = selectedRows[0]
    if (sourceObject?.employee) {
        context.sideRailContext.enableSideRail({
            flow: "VIEW_CICO_DATA",
            employee: referenceableData.employees[getObjectId(sourceObject?.employee)],
            lockedColumns: context.groupKeyInfo,
            employeesInContext: weeklyTkDetailContext?.employeesInContext || [],
        })
    }
}

export const sendWelcomeMail: tButtonClickHandler = (e, { context }) => {
    const selectedIds = context.selectedRows.map((r: tResourceObject) => r.id)
    const singleOrPlural = selectedIds.length === 1 ? "Email" : "Emails"
    API.sendWelcomeEmail({ ids: selectedIds })
        .then(() => {
            context.dispatch(setNotificationMessage(`${singleOrPlural} sent!`, "success", { autoCloseSeconds: 3 }))
        })
        .catch(error => handleError(error, context))
}

export const sendResetPasswordMail: tButtonClickHandler = (e, { context }) => {
    const selectedIds = context.selectedRows.map((r: tResourceObject) => r.id)
    const singleOrPlural = selectedIds.length === 1 ? "Email" : "Emails"
    API.sendResetPasswordEmail({ ids: selectedIds })
        .then(() => {
            context.dispatch(setNotificationMessage(`${singleOrPlural} sent!`, "success", { autoCloseSeconds: 3 }))
        })
        .catch(error => handleError(error, context))
}

export const toggleLogin: tButtonClickHandler = (e, { args, context, gridApi }) => {
    const selectedNodes = gridApi.getSelectedNodes()
    const employeeIds = context.selectedRows.map(e => e.id)
    API.toggleEmployeeLogin({ ids: employeeIds, is_active: args?.is_active })
        .then((response: { [employeeId: number]: { user_id: number; is_active: boolean } }) => {
            selectedNodes.forEach(node => {
                node.data.user_is_active = response[node.data.id].is_active
                node.data.user_id = response[node.data.id].user_id
                node.setData(node.data)
            })
            context.dispatch(setNotificationMessage(`Team Members login ${args?.is_active ? "enabled" : "disabled"}`, "success", { autoCloseSeconds: 3 }))
        })
        .catch(error => handleError(error, context))
}

export const toggleEmployeeStatus: tButtonClickHandler = (e, { args, context, gridApi }) => {
    const selectedNodes = gridApi.getSelectedNodes()
    const employeeIds = context.selectedRows.map(e => e.id)
    if (getFlagEnabled("WA-8655-warn-about-self-deactivation") && employeeIds.includes(context?.currentUser?.employee_id || -1) && !args?.is_active) {
        context.createModalAction({
            title: "Deactivation Warning",
            description: "Are you sure you want to deactivate yourself?",
            action: async () => {
                API.toggleEmployeeStatus({ ids: employeeIds, is_active: args?.is_active })
                    .then(() => {
                        window.location.replace("/rhumbix/")
                    })
                    .catch(error => handleError(error, context))
            },
            buttonClass: "continueButton",
            close: () => {
                context.createModalAction(null)
            },
        })
        return
    }
    API.toggleEmployeeStatus({ ids: employeeIds, is_active: args?.is_active })
        .then((response: { [employeeId: number]: { user_is_active: boolean; is_active: boolean } }) => {
            selectedNodes.forEach(node => {
                node.data.is_active = response[node.data.id].is_active
                node.data.user_is_active = response[node.data.id].user_is_active
                node.setData(node.data)
            })
            context.dispatch(
                setNotificationMessage(`Team Members ${args?.is_active ? "enabled" : "disabled"}`, "success", {
                    autoCloseSeconds: 3,
                })
            )
        })
        .catch(error => handleError(error, context))
}

export const toggleTextAlerts: tButtonClickHandler = (e, { args, context, gridApi }) => {
    const selectedNodes = gridApi.getSelectedNodes()
    const employeeIds = context.selectedRows.map(e => e.id)
    API.toggleTextAlerts({ ids: employeeIds, is_active: args?.is_active })
        .then((response: { [employeeId: number]: { text_alerts_ok: boolean } }) => {
            selectedNodes.forEach(node => {
                node.data.text_alerts_ok = response[node.data.id].text_alerts_ok
                node.setData(node.data)
            })
            context.dispatch(setNotificationMessage(`Team Members text alerts ${args?.is_active ? "enabled" : "disabled"}`, "success", { autoCloseSeconds: 3 }))
        })
        .catch(error => {
            handleError(error, context)
        })
}

const handleError = (error: any, context: tContext) => {
    let errorMsg = error.response.data
    if (errorMsg?.errors) {
        if (Array.isArray(errorMsg.errors)) {
            errorMsg = errorMsg.errors.join("\n")
        } else if (errorMsg.errors.non_field_errors && Array.isArray(errorMsg.errors.non_field_errors)) {
            errorMsg = errorMsg.errors.non_field_errors.join("\n")
        }
    } else if (errorMsg?.message) {
        errorMsg = errorMsg.message
    }
    context.dispatch(setNotificationMessage(errorMsg, "error"))
}

export const exportTK: tButtonClickHandler = (e, { context }) => {
    context.sideRailContext.enableSideRail({
        flow: "RUN_EXPORT",
        selectedExports: context.selectedRows.map(row => {
            return { ...row, label: row.name }
        }),
        cameFrom: "manageExports",
    })
}

// QR Code PDF Lightbox

/**
 * Launch the QR Code modal with the selected employees
 * @param {tMouseEvent<HTMLButtonElement> | null} e Click event.
 * @prop {Object} params View params.
 */
export const openPrintQRModal: tButtonClickHandler = (e, params) => {
    // launch the QR Code modal with the selected employees
    let selectedRows = params?.context?.selectedRows ?? []
    const relatedEmployeeData = params.context?.referenceableData?.employees

    // if we are coming from project employees or cohorts, we won't have all the employee data
    // project employees has an "employee" field, cohorts has "employees" field which is a list of emps in cohort
    if (selectedRows.length && !("cico_qr_code" in selectedRows[0]) && relatedEmployeeData) {
        // project employees
        if ("employee" in selectedRows[0]) {
            selectedRows = selectedRows.map((row: any) => {
                const employeeId = row.employee
                const employeeData = relatedEmployeeData?.[employeeId] ?? {}
                return employeeData ? { ...employeeData, ...row } : row
            })
        }
        // cohorts - we have to dedupe employees that overlap in selected cohorts
        if ("employees" in selectedRows[0]) {
            const allCohortEmployees = selectedRows.flatMap((row: any) => row.employees)
            const uniqueEmployeeIds = Array.from(new Set(allCohortEmployees.map((emp: any) => getObjectId(emp))))
            selectedRows = uniqueEmployeeIds.map(empId => relatedEmployeeData?.[empId])
        }
    }

    params.context.dispatch(openCicoQRLightboxModal(selectedRows))
}

export const viewExportHistory: tButtonClickHandler = async (e, { context }) => {
    context.sideRailContext.enableSideRail({
        flow: "VIEW_TK_EXPORT_HISTORY",
        exportFormat: context.selectedRows[0],
        headerTitle: `${context.selectedRows[0].name} History`,
    })
}

/**
 * Opens the Select Labor Rates modal for the selected timekeeping entries.
 * Allows the user to pick labor rates for ST, OT, and DT to assign
 * as overrides on the selected rows.
 *
 * The button is disabled in the toolbar when the selected rows do not share
 * exactly one project, so we can safely extract a single project ID here.
 * We resolve the project from sourceData (fresh Redux data) rather than the
 * grid node data, which can be stale for newly-added rows.
 */
export const chooseLaborRates: tButtonClickHandler = (e, { context, gridApi, sourceData }) => {
    const selectedRows = gridApi.getSelectedNodes().map(node => node.data)
    if (!selectedRows.length || !context.updateSourceDataCb) return

    const resourceName = context.settings.resources[0] as keyof tSourceData
    const allRows = sourceData[resourceName] ?? []
    const selectedIds = new Set(selectedRows.map((r: tResourceObject) => r.id ?? r.gridId))
    const freshRow = allRows.find((r: tResourceObject) => selectedIds.has(r.id) || selectedIds.has(r.gridId))
    const projectId = freshRow ? getProjectIdFromTKRow(freshRow, context.referenceableData) : null
    if (!projectId) return

    context.dispatch(openChooseLaborRatesModal(selectedRows, projectId))
}

/**
 * Reprices the selected timekeeping entries:
 *   - Sends force_reprice: true to signal the backend to re-run rate matching.
 *   - Nulls saved non-override rate_match_* fields client-side so badges show the
 *     clientside auto-match prediction as a pending preview before the save completes.
 *   - Saved rate overrides and pending clientside overrides are left alone.
 */
export const repriceLaborRates: tButtonClickHandler = (e, { context, gridApi }) => {
    const selectedRows = gridApi.getSelectedNodes().map(node => node.data)
    if (!selectedRows.length || !context.updateSourceDataCb) return

    const laborRateMatches = context.referenceableData?.laborRateMatches ?? {}

    /** Returns true when the saved rate match has no override and should be cleared for preview. */
    const shouldClearRateMatch = (rateMatchId: number | undefined): boolean => {
        if (!rateMatchId) return false
        const match = laborRateMatches[rateMatchId]
        return !match?.labor_rate_override
    }

    const updatedRows = selectedRows.map(entry => ({
        ...entry,
        force_reprice: true,
        // Null non-override saved matches so badges preview the clientside auto-match.
        ...(shouldClearRateMatch(entry.rate_match_st) ? { rate_match_st: null } : {}),
        ...(shouldClearRateMatch(entry.rate_match_ot) ? { rate_match_ot: null } : {}),
        ...(shouldClearRateMatch(entry.rate_match_dt) ? { rate_match_dt: null } : {}),
    }))
    context.updateSourceDataCb({ timekeepingEntries: updatedRows })
}

/**
 * Clears labor rate overrides from the selected timekeeping entries.
 * Only touches fields where an override actually exists:
 *   - Pending clientside override (labor_rate_*_override is a number) → set to null.
 *   - Saved rate override (LaborRateMatch has labor_rate_override) → set clientside field to null.
 * Plain saved matches and untouched clientside fields are left alone.
 */
export const clearLaborRateOverrides: tButtonClickHandler = (e, { context, gridApi }) => {
    const selectedRows = gridApi.getSelectedNodes().map(node => node.data)
    if (!selectedRows.length || !context.updateSourceDataCb) return

    const laborRateMatches = context.referenceableData?.laborRateMatches ?? {}

    /** Returns true when the time type has an override that should be cleared. */
    const hasOverride = (clientsideValue: number | null | undefined, rateMatchId: number | undefined): boolean => {
        if (typeof clientsideValue === "number") return true
        if (rateMatchId) {
            const match = laborRateMatches[rateMatchId]
            if (match?.labor_rate_override) return true
        }
        return false
    }

    const updatedRows = selectedRows.map(entry => ({
        ...entry,
        ...(hasOverride(entry.labor_rate_st_override, entry.rate_match_st) ? { labor_rate_st_override: null } : {}),
        ...(hasOverride(entry.labor_rate_ot_override, entry.rate_match_ot) ? { labor_rate_ot_override: null } : {}),
        ...(hasOverride(entry.labor_rate_dt_override, entry.rate_match_dt) ? { labor_rate_dt_override: null } : {}),
    }))
    context.updateSourceDataCb({ timekeepingEntries: updatedRows })
}

/**
 * Get the set of distinct project IDs from the selected rows, using sourceData
 * for fresh values so that newly-added or recently-edited rows are reflected.
 *
 * Parameters:
 * context (tContext): The current dashboard context (provides selected row IDs and settings).
 * sourceData (tSourceData): The latest row data from the Redux store.
 *
 * Returns:
 * Set<number>: The distinct project IDs across the selected rows.
 */
const getSelectedProjectIds = (context: tContext, sourceData: tSourceData): Set<number> => {
    const resourceName = context.settings.resources[0] as tResourceName
    const allRows: Readonly<tResourceObject[]> = sourceData[resourceName] ?? []
    const selectedIds = new Set(context.selectedRows?.map((r: tResourceObject) => r.id ?? r.gridId))
    const freshSelectedRows = allRows.filter((r: tResourceObject) => selectedIds.has(r.id) || selectedIds.has(r.gridId))
    const projectIds = new Set<number>()
    for (const row of freshSelectedRows) {
        const projectId = getProjectIdFromTKRow(row, context.referenceableData)
        if (!projectId) {
            // Any row without a project invalidates the entire selection —
            // return empty so the caller disables "Set Overrides".
            return new Set<number>()
        }
        projectIds.add(projectId)
    }
    return projectIds
}

/**
 * Factory that builds the dropdown menu items for the three labor-rate
 * toolbar actions: Choose Rates, Reprice Rates, and Clear Overrides.
 *
 * "Choose Rates" is disabled when the selected rows do not belong to
 * exactly one project, since the rate-selection modal requires a single
 * project context. The other two actions are always enabled.
 *
 * Parameters:
 * params (tExtraButtonParams): The toolbar button parameters including
 *     context and sourceData.
 *
 * Returns:
 * tButtonClickHandlerTuple[]: A tuple per action with label, handler,
 *     icon, disabled flag, and tooltip.
 */
export const getLaborRateActions: tButtonClickHandlerFactory = (params: tExtraButtonParams): tButtonClickHandlerTuple[] => {
    const { context, sourceData } = params

    // This factory is eagerly evaluated for every dashboard via rowEventHandlers(),
    // but only applies to the Timekeeping Entries table with rate matching enabled.
    // Exit early for other contexts or when rate matching is disabled.
    if (context.settings.resources[0] !== "timekeepingEntries" || !getFlagEnabled("WA-9079-wage-rates") || !context.currentUser?.features?.rate_tables) {
        return []
    }

    const projectIds = getSelectedProjectIds(context, sourceData)
    const chooseDisabled = projectIds.size !== 1
    const chooseTooltip =
        projectIds.size === 0 ? "Selected rows must have a project" : projectIds.size > 1 ? "Selected rows must all belong to the same project" : "Manually choose rates for the selected rows"

    return [
        ["Reprice", (e, extraParams) => repriceLaborRates(e, extraParams), "refresh", false, "Automatically apply up-to-date labor rates to the selected rows. Overrides will not be changed."],
        ["Set Overrides", (e, extraParams) => chooseLaborRates(e, extraParams), "edit", chooseDisabled, chooseTooltip],
        ["Clear Overrides", (e, extraParams) => clearLaborRateOverrides(e, extraParams), "remove", false, "Clear saved and pending rate overrides from the selected rows"],
    ]
}
